import {
  ArrowsClockwiseIcon,
  CaretDownIcon,
  CaretRightIcon,
  Columns,
  Cylinder,
  DatabaseIcon,
  FileSql,
  FolderSimple,
  HardDrives,
  Plus,
  Table
} from "@phosphor-icons/react";
import {
  Fragment,
  type ReactNode,
  useCallback,
  useEffect,
  useState
} from "react";
import { INTERNAL_HTTP_MESSAGE } from "@workerstation/shared/errors/http";
import { type DatabaseCatalog, type DatabaseInstance as DoInstance, type DatabaseTarget, databaseTargetKey } from "../../api/database/model.ts";
import { itemKey } from "./model.ts";
import { useDatabaseExplorer } from "./react.tsx";

interface DatabaseExplorerProps {
  workspaceId: string | null;
  connectionName: string;
  collapsed: boolean;
  headerLeading?: ReactNode;
  onCollapsedChange: (collapsed: boolean) => void;
  onOpenQuery: (target: DatabaseTarget) => void;
  onOpenTable: (
    target: DatabaseTarget,
    schemaName: string,
    tableName: string
  ) => void;
}

function TreeRow({
  depth,
  label,
  ariaLabel,
  detail,
  icon,
  expandable = false,
  expanded = false,
  active = false,
  title,
  onClick,
  onDoubleClick,
  actions
}: {
  depth: number;
  label: ReactNode;
  ariaLabel?: string;
  detail?: ReactNode;
  icon: ReactNode;
  expandable?: boolean;
  expanded?: boolean;
  active?: boolean;
  title?: string;
  onClick?: () => void;
  onDoubleClick?: () => void;
  actions?: ReactNode;
}) {
  return (
    <div
      role="treeitem"
      aria-expanded={expandable ? expanded : undefined}
      className={`group flex min-h-6 w-full items-center pr-1 text-xs ${
        active
          ? "bg-kumo-elevated text-kumo-default"
          : "text-kumo-subtle hover:bg-kumo-elevated/60 hover:text-kumo-default"
      }`}
      title={title}
    >
      <button
        type="button"
        aria-label={ariaLabel}
        className="flex min-w-0 flex-1 cursor-pointer items-center gap-1 bg-transparent py-[3px] text-left"
        style={{ paddingLeft: 6 + depth * 12 }}
        onClick={onClick}
        onDoubleClick={onDoubleClick}
      >
        <span className="flex h-3 w-3 shrink-0 items-center justify-center">
          {expandable ? (
            expanded ? (
              <CaretDownIcon size={10} weight="bold" />
            ) : (
              <CaretRightIcon size={10} weight="bold" />
            )
          ) : null}
        </span>
        <span className="flex h-[15px] w-[15px] shrink-0 items-center justify-center">
          {icon}
        </span>
        <span className="truncate text-kumo-default">{label}</span>
        {detail ? (
          <span className="truncate text-[10px] text-kumo-subtle">{detail}</span>
        ) : null}
      </button>
      {actions ? (
        <div className="flex shrink-0 items-center opacity-0 group-hover:opacity-100 group-focus-within:opacity-100">
          {actions}
        </div>
      ) : null}
    </div>
  );
}

function TreeAction({
  label,
  onClick,
  children,
  disabled = false
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      className="flex h-5 w-5 cursor-pointer items-center justify-center rounded bg-transparent text-kumo-subtle hover:bg-kumo-fill hover:text-kumo-default disabled:cursor-default disabled:opacity-35"
    >
      {children}
    </button>
  );
}

function FolderRow({
  depth,
  label,
  count,
  rowKey,
  openKeys,
  toggleKey
}: {
  depth: number;
  label: string;
  count?: number;
  rowKey: string;
  openKeys: Set<string>;
  toggleKey: (key: string) => void;
}) {
  return (
    <TreeRow
      depth={depth}
      label={label}
      detail={count === undefined ? undefined : count}
      icon={<FolderSimple size={14} weight="fill" className="text-amber-500" />}
      expandable
      expanded={openKeys.has(rowKey)}
      onClick={() => toggleKey(rowKey)}
    />
  );
}

export function DatabaseExplorer({
  workspaceId,
  connectionName,
  collapsed,
  headerLeading,
  onCollapsedChange,
  onOpenQuery,
  onOpenTable
}: DatabaseExplorerProps) {
  const [openKeys, setOpenKeys] = useState<Set<string>>(() => new Set(["connection", "d1"]));
  const [selectedTarget, setSelectedTarget] = useState<DatabaseTarget | null>(null);
  const { snapshot, dispatch, unavailable } = useDatabaseExplorer(workspaceId);
  const { bindings: d1Bindings, namespaces, instances, facets, catalogs, columns, loadingKeys, registering } = snapshot;
  const error = snapshot.error ?? (unavailable ? INTERNAL_HTTP_MESSAGE : null);
  const toggleKey = useCallback((key: string) => setOpenKeys(current => { const next = new Set(current);if (next.has(key)) next.delete(key);else next.add(key);return next; }), []);
  useEffect(() => { setOpenKeys(new Set(["connection", "d1"]));setSelectedTarget(null); }, [workspaceId]);
  useEffect(() => { setOpenKeys(current => { const next = new Set(current);for (const target of snapshot.autoOpenCatalogs) next.add(itemKey("tables", target));return next; }); }, [snapshot.autoOpenCatalogs]);
  function loadInstances(namespace: string, force = false) { dispatch({ _tag: "LoadInstances", namespace, force }); }
  function loadFacets(namespace: string, instance: DoInstance, force = false) { dispatch({ _tag: "LoadFacets", namespace, instance, force }); }
  function loadColumns(target: DatabaseTarget, tableName: string, force = false) { dispatch({ _tag: "LoadColumns", target, tableName, force }); }
  function loadCatalog(target: DatabaseTarget, force = false) {
    const targetKey = databaseTargetKey(target), openTables = (catalogs.get(targetKey)?.tables ?? []).filter(table => openKeys.has(itemKey("table", targetKey, table.name))).map(table => table.name);
    dispatch({ _tag: "LoadCatalog", target, force, openTables });
  }
  function registerNamespace() { dispatch({ _tag: "RegisterNamespace" }); }
  function registerInstance(namespace: string) { dispatch({ _tag: "RegisterInstance", namespace }); }
  function registerFacet(namespace: string, instance: DoInstance) { dispatch({ _tag: "RegisterFacet", namespace, instance }); }
  function refreshExplorer() { setSelectedTarget(null);setOpenKeys(new Set(["connection", "d1"]));dispatch({ _tag: "Refresh" }); }

  function renderTable(
    target: DatabaseTarget,
    catalog: DatabaseCatalog,
    tableName: string,
    depth: number
  ) {
    const targetKey = databaseTargetKey(target);
    const tableKey = itemKey("table", targetKey, tableName);
    const columnsKey = itemKey("columns-folder", targetKey, tableName);
    const indexesKey = itemKey("indexes", targetKey, tableName);
    const triggersKey = itemKey("triggers", targetKey, tableName);
    const columnMapKey = itemKey(targetKey, "main", tableName);
    const tableColumns = columns.get(columnMapKey);
    const tableIndexes = catalog.indexes.filter(
      (entry) => entry.tableName === tableName
    );
    const tableTriggers = catalog.triggers.filter(
      (entry) => entry.tableName === tableName
    );
    const expanded = openKeys.has(tableKey);

    return (
      <Fragment key={tableName}>
        <TreeRow
          depth={depth}
          label={tableName}
          icon={<Table size={14} />}
          expandable
          expanded={expanded}
          title={`Double-click to open ${tableName}`}
          onClick={() => {
            const opening = !expanded;
            toggleKey(tableKey);
            if (opening) {
              setOpenKeys((current) => {
                const next = new Set(current);
                next.add(columnsKey);
                return next;
              });
              void loadColumns(target, tableName);
            }
          }}
          onDoubleClick={() => onOpenTable(target, "main", tableName)}
          actions={
            <TreeAction
              label={`Open table ${tableName}`}
              onClick={() => onOpenTable(target, "main", tableName)}
            >
              <Table size={12} />
            </TreeAction>
          }
        />
        {expanded ? (
          <>
            <FolderRow
              depth={depth + 1}
              label="Columns"
              count={tableColumns?.length}
              rowKey={columnsKey}
              openKeys={openKeys}
              toggleKey={toggleKey}
            />
            {openKeys.has(columnsKey) ? (
              tableColumns ? (
                tableColumns.map((column) => (
                  <TreeRow
                    key={column.name}
                    depth={depth + 2}
                    label={column.name}
                    detail={`(${column.type})${column.primaryKey ? " PK" : ""}`}
                    icon={<Columns size={13} />}
                  />
                ))
              ) : (
                <TreeRow
                  depth={depth + 2}
                  label={
                    loadingKeys.has(itemKey("columns", columnMapKey))
                      ? "Loading columns…"
                      : "No columns"
                  }
                  icon={<Columns size={13} />}
                />
              )
            ) : null}
            <FolderRow
              depth={depth + 1}
              label="Indexes"
              count={tableIndexes.length}
              rowKey={indexesKey}
              openKeys={openKeys}
              toggleKey={toggleKey}
            />
            {openKeys.has(indexesKey)
              ? tableIndexes.map((index) => (
                  <TreeRow
                    key={index.name}
                    depth={depth + 2}
                    label={index.name}
                    icon={<Columns size={13} />}
                  />
                ))
              : null}
            <FolderRow
              depth={depth + 1}
              label="Triggers"
              count={tableTriggers.length}
              rowKey={triggersKey}
              openKeys={openKeys}
              toggleKey={toggleKey}
            />
            {openKeys.has(triggersKey)
              ? tableTriggers.map((trigger) => (
                  <TreeRow
                    key={trigger.name}
                    depth={depth + 2}
                    label={trigger.name}
                    icon={<FileSql size={13} />}
                  />
                ))
              : null}
          </>
        ) : null}
      </Fragment>
    );
  }

  function renderTarget(
    target: DatabaseTarget,
    depth: number,
    presentation?: {
      label: ReactNode;
      detail?: ReactNode;
      icon?: ReactNode;
    }
  ) {
    const targetKey = databaseTargetKey(target);
    const rowKey = itemKey("target", targetKey);
    const tablesKey = itemKey("tables", targetKey);
    const catalog = catalogs.get(targetKey);
    const expanded = openKeys.has(rowKey);
    const selected = selectedTarget
      ? databaseTargetKey(selectedTarget) === targetKey
      : false;

    return (
      <Fragment key={targetKey}>
        <TreeRow
          depth={depth}
          label={
            presentation?.label ??
            (target.facet === "root" ? "root" : target.facet)
          }
          detail={
            presentation?.detail ??
            (target.facet === "root" ? "storage" : "facet")
          }
          icon={
            presentation?.icon ?? <Cylinder size={14} weight="fill" />
          }
          expandable
          expanded={expanded}
          active={selected}
          onClick={() => {
            const opening = !expanded;
            setSelectedTarget(target);
            toggleKey(rowKey);
            if (opening) void loadCatalog(target);
          }}
          actions={
            <>
              <TreeAction
                label="New query"
                onClick={() => onOpenQuery(target)}
              >
                <FileSql size={12} />
              </TreeAction>
              <TreeAction
                label="Refresh schema"
                onClick={() => void loadCatalog(target, true)}
              >
                <ArrowsClockwiseIcon size={12} />
              </TreeAction>
            </>
          }
        />
        {expanded ? (
          catalog ? (
            <>
              <FolderRow
                depth={depth + 1}
                label="Tables"
                count={catalog.tables.length}
                rowKey={tablesKey}
                openKeys={openKeys}
                toggleKey={toggleKey}
              />
              {openKeys.has(tablesKey)
                ? catalog.tables.map((table) =>
                    renderTable(target, catalog, table.name, depth + 2)
                  )
                : null}
            </>
          ) : (
            <TreeRow
              depth={depth + 1}
              label={
                loadingKeys.has(itemKey("catalog", targetKey))
                  ? "Loading schema…"
                  : "Schema unavailable"
              }
              icon={<ArrowsClockwiseIcon size={13} />}
            />
          )
        ) : null}
      </Fragment>
    );
  }

  return (
    <section
      className="flex h-full min-h-0 w-full flex-col border-t border-kumo-line"
      onContextMenu={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
    >
      <div className="flex h-7 shrink-0 items-center border-b border-kumo-line/60 px-1">
        {headerLeading ?? (
          <button
            type="button"
            aria-expanded={!collapsed}
            onClick={() => onCollapsedChange(!collapsed)}
            className="flex min-w-0 flex-1 cursor-pointer items-center gap-1 bg-transparent px-1 text-left text-[10px] font-semibold uppercase tracking-wide text-kumo-default"
          >
            {collapsed ? (
              <CaretRightIcon size={10} weight="bold" />
            ) : (
              <CaretDownIcon size={10} weight="bold" />
            )}
            Databases
          </button>
        )}
        <TreeAction
          label="New query"
          disabled={!selectedTarget}
          onClick={() => {
            if (selectedTarget) onOpenQuery(selectedTarget);
          }}
        >
          <FileSql size={13} />
        </TreeAction>
        <TreeAction
          label="Register namespace"
          disabled={!workspaceId || registering}
          onClick={() => void registerNamespace()}
        >
          <Plus size={13} weight="bold" />
        </TreeAction>
        <TreeAction label="Refresh databases" onClick={refreshExplorer}>
          <ArrowsClockwiseIcon
            size={13}
            className={loadingKeys.has("namespaces") ? "animate-spin" : ""}
          />
        </TreeAction>
      </div>

      {!collapsed ? (
        <div role="tree" className="min-h-0 flex-1 overflow-auto py-1">
          {workspaceId ? (
            <>
              <TreeRow
                depth={0}
                label={connectionName || "Workerspace"}
                detail="connected"
                icon={
                  <span className="relative">
                    <HardDrives size={14} />
                    <span className="absolute -right-0.5 -bottom-0.5 h-1.5 w-1.5 rounded-full bg-status-success ring-1 ring-kumo-base" />
                  </span>
                }
                expandable
                expanded={openKeys.has("connection")}
                onClick={() => toggleKey("connection")}
              />
              {openKeys.has("connection") ? (
                <>
                  {d1Bindings.length ? (
                    <>
                      <TreeRow
                        depth={1}
                        label="D1"
                        detail={d1Bindings.length}
                        icon={<DatabaseIcon size={14} weight="fill" />}
                        expandable
                        expanded={openKeys.has("d1")}
                        onClick={() => toggleKey("d1")}
                      />
                      {openKeys.has("d1")
                        ? d1Bindings.map((binding) =>
                            renderTarget(
                              {
                                workspaceId,
                                kind: "d1",
                                namespace: binding.bindingName,
                                instance: binding.namespace,
                                facet: "root"
                              },
                              2,
                              {
                                label: binding.bindingName,
                                detail:
                                  binding.databaseName ?? binding.namespace,
                                icon: <Cylinder size={14} weight="fill" />
                              }
                            )
                          )
                        : null}
                    </>
                  ) : null}
                  {namespaces.length ? (
                    namespaces.map((namespace) => {
                    const namespaceKey = itemKey("namespace", namespace.className);
                    const namespaceInstances = instances.get(namespace.className);
                    const loadingKey = itemKey("instances", namespace.className);
                    return (
                      <Fragment key={namespace.className}>
                        <TreeRow
                          depth={1}
                          label={namespace.className}
                          ariaLabel={namespace.className}
                          detail={namespace.source}
                          icon={<DatabaseIcon size={14} />}
                          expandable
                          expanded={openKeys.has(namespaceKey)}
                          onClick={() => {
                            const opening = !openKeys.has(namespaceKey);
                            toggleKey(namespaceKey);
                            if (opening) void loadInstances(namespace.className);
                          }}
                          actions={
                            <TreeAction
                              label={`Add instance to ${namespace.className}`}
                              disabled={registering}
                              onClick={() => void registerInstance(namespace.className)}
                            >
                              <Plus size={12} />
                            </TreeAction>
                          }
                        />
                        {openKeys.has(namespaceKey) ? (
                          namespaceInstances ? (
                            namespaceInstances.length ? (
                              namespaceInstances.map((instance) => {
                                const instanceKey = itemKey(
                                  "instance",
                                  namespace.className,
                                  instance.id
                                );
                                const facetMapKey = itemKey(
                                  namespace.className,
                                  instance.id
                                );
                                const instanceFacets = facets.get(facetMapKey);
                                return (
                                  <Fragment key={instance.id}>
                                    <TreeRow
                                      depth={2}
                                      label={instance.name ?? instance.id}
                                      ariaLabel={instance.name ?? instance.id}
                                      detail={instance.name ? instance.via : instance.id}
                                      icon={<HardDrives size={14} />}
                                      expandable
                                      expanded={openKeys.has(instanceKey)}
                                      onClick={() => {
                                        const opening = !openKeys.has(instanceKey);
                                        toggleKey(instanceKey);
                                        if (opening) {
                                          void loadFacets(namespace.className, instance);
                                        }
                                      }}
                                      actions={
                                        <TreeAction
                                          label={`Add facet to ${instance.name ?? instance.id}`}
                                          disabled={registering}
                                          onClick={() =>
                                            void registerFacet(namespace.className, instance)
                                          }
                                        >
                                          <Plus size={12} />
                                        </TreeAction>
                                      }
                                    />
                                    {openKeys.has(instanceKey) ? (
                                      instanceFacets ? (
                                        instanceFacets.map((facet) =>
                                          renderTarget(
                                            {
                                              workspaceId,
                                              namespace: namespace.className,
                                              instance: instance.id,
                                              facet: facet.name || "root"
                                            },
                                            3
                                          )
                                        )
                                      ) : (
                                        <TreeRow
                                          depth={3}
                                          label={
                                            loadingKeys.has(
                                              itemKey(
                                                "facets",
                                                namespace.className,
                                                instance.id
                                              )
                                            )
                                              ? "Loading storage…"
                                              : "No storage"
                                          }
                                          icon={<Cylinder size={13} />}
                                        />
                                      )
                                    ) : null}
                                  </Fragment>
                                );
                              })
                            ) : (
                              <TreeRow
                                depth={2}
                                label="No instances — use + to add one"
                                icon={<HardDrives size={13} />}
                              />
                            )
                          ) : (
                            <TreeRow
                              depth={2}
                              label={
                                loadingKeys.has(loadingKey)
                                  ? "Loading instances…"
                                  : "No instances"
                              }
                              icon={<HardDrives size={13} />}
                            />
                          )
                        ) : null}
                      </Fragment>
                    );
                    })
                  ) : null}
                  {d1Bindings.length === 0 && namespaces.length === 0 ? (
                    <div className="mx-2 mt-2 rounded border border-dashed border-kumo-line p-2 text-[11px] leading-4 text-kumo-subtle">
                      Declare a D1 binding or run a Worker that exports a Durable
                      Object, then refresh to browse its SQLite storage.
                      <button
                        type="button"
                        disabled={registering}
                        onClick={() => void registerNamespace()}
                        className="mt-1 block cursor-pointer bg-transparent text-kumo-accent disabled:cursor-default disabled:opacity-35"
                      >
                        Register a namespace manually
                      </button>
                    </div>
                  ) : null}
                </>
              ) : null}
            </>
          ) : (
            <p className="p-2 text-[11px] text-kumo-subtle">
              Open a workerspace to browse databases.
            </p>
          )}
          {error ? (
            <div className="m-2 rounded bg-red-500/10 p-2 text-[11px] text-status-error">
              {error}
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
